import { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import QRCode from "react-qr-code";
import {
  getAdminWithdrawals,
  approveAdminWithdrawal,
  rejectAdminWithdrawal,
} from "../../services/adminWithdrawalService";

/* ─── helpers ─── */
const toastCfg = { autoClose: 1500 };

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Detail Modal ─── */
const DetailModal = ({ selected, onHide, onApprove, onReject }) => {
  const [remark, setRemark] = useState("");

  useEffect(() => {
    if (selected) setRemark("");
  }, [selected]);

  if (!selected) return null;

  const isPending = selected.status === "pending";

  return (
    <Modal show={!!selected} onHide={onHide} centered size="lg" backdrop="static">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>Withdrawal Details</div>
          <div style={ms.headerSub}>ID #{selected.id} · {selected.method}</div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>

      <div style={ms.body}>
        <div style={ms.grid}>
          <div>
            <label style={ms.label}>User</label>
            <div style={ms.readField}>{selected.user?.name || "—"}</div>
          </div>
          <div>
            <label style={ms.label}>Amount</label>
            <div style={ms.readField}>₹{selected.amount}</div>
          </div>
          <div>
            <label style={ms.label}>Method</label>
            <div style={ms.readField}>{selected.method}</div>
          </div>
          <div>
            <label style={ms.label}>Date</label>
            <div style={ms.readField}>{selected.created_at}</div>
          </div>

          {selected.method === "UPI" ? (
            <>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={ms.label}>UPI ID</label>
                <div style={ms.readField}>{selected.details?.upi_id || "—"}</div>
              </div>
              {selected.details?.upi_id && (
                <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "16px 0 4px" }}>
                  <div style={ms.qrBox}>
                    <QRCode
                      value={`upi://pay?pa=${selected.details.upi_id}&pn=${encodeURIComponent(selected.user?.name || "")}&am=${selected.amount}&cu=INR`}
                      size={180}
                      bgColor="#ffffff"
                      fgColor="#0f172a"
                      level="M"
                    />
                  </div>
                  <div style={ms.qrCaption}>
                    Scan to pay <strong>₹{Number(selected.amount).toLocaleString("en-IN")}</strong> to <strong>{selected.details.upi_id}</strong>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label style={ms.label}>Bank Name</label>
                <div style={ms.readField}>{selected.details?.bank_name || "—"}</div>
              </div>
              <div>
                <label style={ms.label}>Account No</label>
                <div style={ms.readField}>{selected.details?.acc_no || "—"}</div>
              </div>
              <div>
                <label style={ms.label}>IFSC Code</label>
                <div style={{ ...ms.readField, fontFamily: "monospace", letterSpacing: "0.08em" }}>{selected.details?.ifsc || "—"}</div>
              </div>
              <div>
                <label style={ms.label}>Account Holder</label>
                <div style={ms.readField}>{selected.details?.holder || "—"}</div>
              </div>
            </>
          )}

          {isPending && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={ms.label}>Reject Remark</label>
              <input
                style={ms.input}
                placeholder="Enter remark (required to reject)"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      <div style={ms.footer}>
        <button style={ms.cancelBtn} onClick={onHide}>Close</button>
        {isPending && (
          <>
            <button style={ms.rejectBtn} onClick={() => onReject(selected.id, remark)}>
              <Icon icon="solar:close-circle-bold" style={{ fontSize: 15 }} />
              Reject
            </button>
            <button style={ms.approveBtn} onClick={() => onApprove(selected.id)}>
              <Icon icon="solar:check-circle-bold" style={{ fontSize: 15 }} />
              Approve
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

/* ─── Modal styles ─── */
const ms = {
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9",
  },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub:   { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: {
    background: "#f1f5f9", border: "none", borderRadius: 8,
    width: 32, height: 32, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b",
  },
  body:  { padding: "18px 24px" },
  grid:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 18px" },
  label: {
    display: "block", fontSize: "0.75rem", fontWeight: 700,
    color: "#475569", marginBottom: 5,
    textTransform: "uppercase", letterSpacing: "0.04em",
  },
  readField: {
    padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc",
    fontFamily: "inherit",
  },
  input: {
    width: "100%", padding: "9px 12px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc",
    outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  },
  footer: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "14px 24px 20px", borderTop: "1px solid #f1f5f9",
  },
  cancelBtn: {
    padding: "8px 18px", background: "#f1f5f9", border: "none",
    borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem",
    fontWeight: 600, color: "#475569", cursor: "pointer",
  },
  approveBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 20px", background: "linear-gradient(135deg,#16632b,#1da144)",
    border: "none", borderRadius: 8, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer",
  },
  rejectBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 20px", background: "linear-gradient(135deg,#991b1b,#dc2626)",
    border: "none", borderRadius: 8, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer",
  },
  qrBox: {
    padding: 16,
    background: "#fff",
    border: "1.5px solid #e2e8f0",
    borderRadius: 14,
    display: "inline-flex",
    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
  },
  qrCaption: {
    fontSize: "0.81rem",
    color: "#64748b",
    textAlign: "center",
    lineHeight: 1.5,
  },
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export const WithdrawalsLayer = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState("all");
  const [method, setMethod] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [selected, setSelected] = useState(null);

  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  /* ── fetch ── */
  const loadWithdrawals = useCallback(async () => {
    try {
      setLoading(true);
      const res =
        status === "all"
          ? await getAdminWithdrawals()
          : await getAdminWithdrawals(status);
      if (res.status) setData(res.data || []);
      else toast.error("Failed to load data", toastCfg);
    } catch {
      toast.error("Server error", toastCfg);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadWithdrawals();
    const interval = setInterval(loadWithdrawals, 10000);
    return () => clearInterval(interval);
  }, [loadWithdrawals]);

  /* ── actions ── */
  const handleApprove = async (id) => {
    if (!window.confirm("Approve withdrawal?")) return;
    const res = await approveAdminWithdrawal(id);
    if (res.status) {
      toast.success("Withdrawal approved", toastCfg);
      setSelected(null);
      loadWithdrawals();
    }
  };

  const handleReject = async (id, remark) => {
    if (!remark) return toast.error("Enter rejection remark", toastCfg);
    const res = await rejectAdminWithdrawal(id, remark);
    if (res.status) {
      toast.success("Withdrawal rejected", toastCfg);
      setSelected(null);
      loadWithdrawals();
    }
  };

  /* ── client-side method + date filter ── */
  const filteredData = useMemo(() => {
    let d = method === "all" ? data : data.filter((i) => i.method === method);
    if (fromDate) d = d.filter((i) => (i.created_at || "").slice(0, 10) >= fromDate);
    if (toDate)   d = d.filter((i) => (i.created_at || "").slice(0, 10) <= toDate);
    return d;
  }, [data, method, fromDate, toDate]);

  /* ── stats ── */
  const stats = useMemo(() => ({
    total:    data.length,
    pending:  data.filter((i) => i.status === "pending").length,
    approved: data.filter((i) => i.status === "approved").length,
    rejected: data.filter((i) => i.status === "rejected").length,
  }), [data]);

  /* ── columns ── */
  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 44,
      enableSorting: false,
      cell: ({ row }) =>
        row.index + 1 + pagination.pageIndex * pagination.pageSize,
    },
    {
      accessorKey: "id",
      header: "ID",
      size: 70,
      cell: ({ getValue }) => (
        <span style={ts.uid}>#{getValue()}</span>
      ),
    },
    {
      accessorKey: "user",
      header: "User",
      cell: ({ getValue }) => {
        const user = getValue();
        return <span style={ts.name}>{user?.name || "—"}</span>;
      },
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ getValue }) => (
        <span style={ts.wallet}>₹{Number(getValue() || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      accessorKey: "method",
      header: "Method",
      cell: ({ getValue }) => (
        <span style={{ ...ts.badge, background: "#eff6ff", color: "#2563eb" }}>
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue, row }) => {
        const s = getValue();
        const color = row.original.status_color;
        const bg =
          s === "approved" ? "#dcfce7" :
          s === "rejected" ? "#fee2e2" : "#fef9c3";
        const fg =
          s === "approved" ? "#16a34a" :
          s === "rejected" ? "#dc2626" : "#92400e";
        return (
          <span style={{ ...ts.badge, background: color ? undefined : bg, color: color ? "#fff" : fg, ...(color ? { background: color } : {}) }}>
            {s === "pending" ? "● Pending" : s === "approved" ? "● Approved" : "○ Rejected"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 90,
      enableSorting: false,
      cell: ({ row }) => (
        <button style={ts.viewBtn} onClick={() => setSelected(row.original)}>
          <Icon icon="solar:eye-bold" style={{ fontSize: 13 }} />
          View
        </button>
      ),
    },
  ], [pagination.pageIndex, pagination.pageSize]);

  /* ── table instance ── */
  const table = useReactTable({
    data: filteredData,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel:       getCoreRowModel(),
    getFilteredRowModel:   getFilteredRowModel(),
    getSortedRowModel:     getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      return (
        row.original.user?.name?.toLowerCase().includes(v) ||
        String(row.original.id).includes(v)
      );
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();

  return (
    <div style={s.root}>
      {/* ── Stats row ── */}
      <div style={s.statsRow}>
        {[
          { label: "Total",    value: stats.total,    icon: "solar:wallet-bold-duotone",          color: "#4B5EAA" },
          { label: "Pending",  value: stats.pending,  icon: "solar:clock-circle-bold-duotone",    color: "#F0A500" },
          { label: "Approved", value: stats.approved, icon: "solar:check-circle-bold-duotone",    color: "#16a34a" },
          { label: "Rejected", value: stats.rejected, icon: "solar:close-circle-bold-duotone",    color: "#dc2626" },
        ].map((c) => (
          <div key={c.label} style={s.statCard}>
            <div style={{ ...s.statIcon, background: `${c.color}14`, color: c.color }}>
              <Icon icon={c.icon} style={{ fontSize: 20 }} />
            </div>
            <div>
              <div style={s.statValue}>{c.value}</div>
              <div style={s.statLabel}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Card ── */}
      <div style={s.card}>
        {/* Toolbar */}
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search user or ID…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>

            <select style={s.select} value={status}
              onChange={(e) => { setStatus(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <select style={s.select} value={method}
              onChange={(e) => { setMethod(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="all">All Method</option>
              <option value="UPI">UPI</option>
              <option value="BANK">BANK</option>
            </select>

            <div style={s.dateGroup}>
              <span style={s.dateLabel}>From</span>
              <input type="date" style={s.dateInput} value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }} />
            </div>
            <div style={s.dateGroup}>
              <span style={s.dateLabel}>To</span>
              <input type="date" style={s.dateInput} value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }} />
            </div>
          </div>

          <div style={s.toolbarRight}>
            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:wallet-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No withdrawals match your filters
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>{pageIndex * pageSize + 1}–{Math.min((pageIndex + 1) * pageSize, filteredData.length)}</strong>
            {" "}of <strong>{filteredData.length}</strong> withdrawals
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}          disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}            disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const pg = Math.max(0, Math.min(pageIndex - 3, pageCount - 7)) + i;
              return (
                <button key={pg} style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <DetailModal
        selected={selected}
        onHide={() => setSelected(null)}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
};

/* ─── Page styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },

  statsRow: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16 },
  statCard: {
    background: "#fff", borderRadius: 14, padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 12,
    border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  statIcon:  { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statValue: { fontSize: "1.4rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  statLabel: { fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 },

  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },

  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px", borderBottom: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 10,
  },
  toolbarLeft:  { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },

  searchWrap:  { position: "relative", display: "flex", alignItems: "center" },
  searchIcon:  { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: {
    padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.82rem", color: "#334155", background: "#f8fafc",
    outline: "none", width: 220, fontFamily: "inherit",
  },
  clearBtn: {
    position: "absolute", right: 8, background: "none", border: "none",
    cursor: "pointer", color: "#94a3b8", padding: 0,
    display: "flex", alignItems: "center",
  },
  select: {
    padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", color: "#475569", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit",
  },

  dateGroup: { display: "flex", alignItems: "center", gap: 5 },
  dateLabel: { fontSize: "0.78rem", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" },
  dateInput: {
    padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", color: "#475569", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit",
  },

  tableWrap: { overflowX: "auto" },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: {
    padding: "10px 14px", background: "#f8fafc", color: "#64748b",
    fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase",
    letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9",
    whiteSpace: "nowrap", textAlign: "left",
  },
  td: {
    padding: "9px 14px", borderBottom: "1px solid #f8fafc",
    color: "#334155", verticalAlign: "middle",
  },
  emptyCell: { padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },

  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 8,
  },
  pgInfo:      { fontSize: "0.78rem", color: "#64748b" },
  pgBtns:      { display: "flex", gap: 4 },
  pgBtn: {
    minWidth: 30, height: 30, border: "1px solid #e2e8f0",
    background: "#fff", borderRadius: 7, cursor: "pointer",
    fontSize: "0.78rem", color: "#475569", fontFamily: "inherit",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
  },
  pgBtnActive: {
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", border: "none", fontWeight: 700,
  },
};

/* ─── Table cell styles ─── */
const ts = {
  uid:    { fontSize: "0.76rem", color: "#94a3b8", fontFamily: "monospace" },
  name:   { fontWeight: 600, color: "#1e293b", fontSize: "0.83rem" },
  wallet: { fontWeight: 700, color: "#16632b", fontSize: "0.83rem" },
  badge: {
    display: "inline-flex", alignItems: "center",
    padding: "3px 9px", borderRadius: 100,
    fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap",
  },
  viewBtn: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 10px",
    background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe",
    borderRadius: 7, cursor: "pointer", fontFamily: "inherit",
    fontSize: "0.75rem", fontWeight: 600,
  },
};
